<script lang="ts">
import CommonButton from '@/components/common/button/CommonButton.vue'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { defineComponent, type PropType, computed } from 'vue'

export default defineComponent({
  name: 'ImageCarousel',
  props: {
    slides: {
      type: Array as PropType<string[]>,
      required: true
    },
    selectedImageIndex: {
      type: Number,
      required: true
    }
  },
  emits: ['closeCarousel', 'deleteImage', 'update:selectedImageIndex'],
  setup(props, { emit }) {
    const commonModalStore = useCommonModalStore()

    // FIXME: カルーセルでのスライドを有効化するにはcurrentSlideをrefプロパティにする
    /** 表示中の画像インデックス */
    const currentSlide = computed(() => props.selectedImageIndex)
    /** 画像リスト */
    const imageSlides = computed(() => props.slides)

    /** 画像なしフラグ */
    const noImageFlag = computed(() => {
      return imageSlides.value.length === 0
    })
    /** 拡大画像の幅 */
    const imageWidth: number = 450
    /** 拡大画像の高さ */
    const imageHeight: number = 450

    /**
     * カルーセルを閉じる
     * @param なし
     * @returns なし
     */
    const closeCarousel = (): void => {
      emit('closeCarousel')
    }

    /**
     * 画像削除処理
     * @param なし
     * @returns なし
     */
    const deleteImage = (): void => {
      commonModalStore
        .openConfirmModal({
          title: '選択した写真を削除しますか？',
          body: '注意：一度削除すると元に戻せません',
          okTitle: 'はい',
          okColor: 'danger',
          cancelTitle: 'いいえ',
          centered: true
        })
        .then(() => {
          emit('deleteImage', currentSlide.value)
        })
    }

    return {
      currentSlide,
      imageSlides,
      imageWidth,
      imageHeight,
      noImageFlag,
      closeCarousel,
      deleteImage,
      CommonButton
    }
  }
})
</script>

<template>
  <div class="overlay-container">
    <div class="carousel-with-buttons">
      <b-carousel id="carousel-1" v-model="currentSlide" controls indicators :interval="0">
        <b-carousel-slide
          v-for="(slide, index) in imageSlides"
          :key="index"
          class="carouselSlideContainer"
        >
          <template #img>
            <div class="slideContainer">
              <img
                :src="slide"
                alt="Slide image"
                class="slide-image"
                :style="{ width: `${imageWidth}px`, height: `${imageHeight}px` }"
              />
            </div>
          </template>
        </b-carousel-slide>
      </b-carousel>

      <div class="btn-container">
        <CommonButton variant="secondary" class="close-button px-3" @click="closeCarousel"
          >閉じる</CommonButton
        >
        <CommonButton
          variant="danger"
          class="delete-button"
          :disabled="noImageFlag"
          @click="deleteImage"
          >削除</CommonButton
        >
      </div>
    </div>
    <div v-if="noImageFlag">
      <span>NoImage</span>
    </div>
  </div>
</template>

<style scoped>
/* 親コンポーネントの前面に表示するオーバーレイスタイル */
.overlay-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7); /* 背景の暗いオーバーレイ */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}
.carouselSlideContainer {
  background: var(--dis-midgray) !important;
}
.slideImage {
  width: 1000px;
  height: auto;
  object-fit: contain;
  margin: 40px 8px 116px;
}
/** カルーセルの矢印などを変更するための設定 */
:deep(.carousel-control-prev) {
  justify-content: flex-start !important;
  padding-left: 2% !important;
}
:deep(carousel-control-next) {
  justify-content: flex-end !important;
  padding-right: 2% !important;
}
:deep(.carousel-control-prev-icon),
:deep(.carousel-control-next-icon) {
  height: 20% !important;
  width: 20% !important;
}
.carousel-with-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  max-width: 100%;
}
.carousel-with-buttons b-carousel {
  width: 100%;
  max-width: 100%;
}
.btn-container {
  margin-top: 1rem;
  display: flex;
  justify-content: space-around;
  width: 100%;
  max-width: 100%;
  align-items: center;
}
.btn-container > * {
  width: 20%;
  max-width: 300px;
}
</style>
